import React, { Children } from 'react';
import PropTypes from 'prop-types';
import { IntlProvider } from 'react-intl';
import deepForceUpdate from 'react-deep-force-update';
import { Provider } from 'rebass';
import * as analytics from './../core/analytics';
import { colors } from './../tailwind';
import { getp } from '../helpers/getProp';
import Whitespace from '../components_new/common/Whitespace';
import Text from '../components_new/common/Text';
import Button from '../components_new/common/FormComponents/Button';
import { Provider as MobxProvider, observer, inject } from 'mobx-react';
import ScrollTrigger from 'react-scroll-trigger';
import history from '../core/history';
import mobxStores from '../mobxStores';
import mobileInfo from './../mobileInfo';
import FullStory from 'react-fullstory';
import HowItCarousel from '../components_new/HowItCarousel';
import { observable, action } from 'mobx';
import { View } from '../components_new/common';
import Typewriter from '../components_new/Typewriter';
import Link from './Link';

const ContextType = {
  // Enables critical path CSS rendering
  // https://github.com/kriasoft/isomorphic-style-loader
  insertCss: PropTypes.func.isRequired,
  // Integrate Redux
  // http://redux.js.org/docs/basics/UsageWithReact.html
  store: PropTypes.shape({
    subscribe: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    getState: PropTypes.func.isRequired,
  }).isRequired,
  // Apollo Client
  client: PropTypes.object.isRequired,
};

/**
 * The top-level React component setting context (global) variables
 * that can be accessed from all the child components.
 *
 * https://facebook.github.io/react/docs/context.html
 *
 * Usage example:
 *
 *   const context = {
 *     history: createBrowserHistory(),
 *     store: createStore(),
 *   };
 *
 *   ReactDOM.render(
 *     <App context={context}>
 *       <Layout>
 *         <LandingPage />
 *       </Layout>
 *     </App>,
 *     container,
 *   );
 */
class App extends React.PureComponent {
  static propTypes = {
    context: PropTypes.shape(ContextType).isRequired,
    children: PropTypes.element.isRequired,
  };

  static childContextTypes = ContextType;

  state = {
    windowWidth: 0,
    mobileInfo: null,
  };

  getChildContext() {
    return this.props.context;
  }

  componentDidMount() {
    const store = this.props.context && this.props.context.store;
    if (store) {
      this.unsubscribe = store.subscribe(() => {
        const state = store.getState();
        const newIntl = state.intl;
        if (this.intl !== newIntl) {
          this.intl = newIntl;
          if (__DEV__) {
            // eslint-disable-next-line no-console
            console.log('Intl changed — Force rendering');
          }
          deepForceUpdate(this);
        }
      });
    }
    const userId = getp(store.getState(), 'account.data.userId');
    analytics.setUserId(userId);

    this.updateWindowDimensions();
    if (window) {
      window.addEventListener('resize', this.updateWindowDimensions);
    }

    this.setState({ mobileInfo: mobileInfo(window.navigator.userAgent) });
  }

  updateWindowDimensions = () => {
    this.setState({
      windowWidth: window.innerWidth,
    });
  };

  componentWillUnmount() {
    if (window) {
      window.removeEventListener('resize', this.updateWindowDimensions);
    }
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }
//here
  render() {
    // NOTE: If you need to add or modify header, footer etc. of the app,
    // please do that inside the Layout component.
    const store = this.props.context && this.props.context.store;
    const state = store && store.getState();
    this.intl = (state && state.intl) || {};
    const { initialNow, locale, messages } = this.intl;
    const localeMessages = (messages && messages[locale]) || {};
    const userAccount = getp(state, 'account.data');

    const { isMobile, serverSide } = this.props;
    const pathName = getp(history, 'location.pathname');
    let childs;
    const isSmall = isMobile || (!serverSide && this.state.windowWidth < 900);
    if (isSmall) {
      if (['/policies', '/privacy', '/user/verification'].indexOf(pathName) < 0) {
        // console.log('Rending Mobile App');
        childs = <MobileApp windowWidth={this.state.windowWidth} />;
      } else {
        // console.log('rendering desktop');
        childs = React.cloneElement(this.props.children, { isSmall });
      }
    } else {
      // console.log('rendering desktop');
      childs = React.cloneElement(this.props.children, { isSmall });
    }

    return (
      <MobxProvider {...mobxStores}>
        <IntlProvider
          initialNow={initialNow}
          locale={locale}
          messages={localeMessages}
          defaultLocale="en-US"
          id="intl-provider"
        >
          <Provider
            id="rebass-provider"
            theme={{
              colors,
              space: [0, 2, 4, 8, 16, 24, 32, 64, 128],
              'font-family': 'Metropolis',
            }}
          >
            {childs}
            <UserLoginLoader userAccount={userAccount} />
          </Provider>
        </IntlProvider>
      </MobxProvider>
    );
  }
}

export default App;

@inject('userStore', 'inboxStore')
@observer
class UserLoginLoader extends React.Component {
  componentDidMount() {
    const { userStore, userAccount, inboxStore } = this.props;
    console.log('userStore, userAccount', userStore, userAccount);
    if (userAccount && userAccount.userId) {
      userStore.login(userAccount.userId, userAccount);
    } else {
      userStore.login(false);
    }
    inboxStore.startCountPolling();
  }

  render() {
    return null;
  }
}

@observer
export class MobileApp extends React.Component {
  @observable owner = false;
  @observable isBottom = false;

  mapSizeClassToDimensions = sizeClass => {
    switch (sizeClass) {
      case 1:
      case 2:
        return '300';
      case 3:
        return '450';
      case 4:
        return '600';
      default:
        return '900';
    }
  };

  trackStoreClick = storetype => {
    console.log('Tracking storetype');
    analytics.trackAppDownloadClick(storetype);
  };

  @action
  bottom = isBottom => {
    this.isBottom = isBottom;
  };

  render() {
    const os = getp(this.state, 'mobileInfo.os', 'unknown');
    const { windowWidth } = this.props;

    const imgDim = this.mapSizeClassToDimensions(Math.ceil(windowWidth / 150));

    const imgSrc = `../../images/mobileWebLayout/splash-${imgDim}.png`;
    const widthBase = Math.floor(windowWidth * 0.5);
    const iosWidth = Math.floor(widthBase * 0.8);
    const isIos = os === 'iOS';
    const appButton = (
      <div className="f f-cc f-rows" style={{ marginBottom: '8px' }}>
        <div className="f f-columns f-sc">
          <a
            href="itms-apps://itunes.apple.com/us/app/yoodlize/id1448525055?ls=1&mt=8"
            onClick={() => this.trackStoreClick('Apple')}
          >
            <img
              alt="Get it on the Apple Store"
              src={`/images/third-party/apple/Download_on_the_App_Store_Badge_US-UK_RGB_blk.svg`}
              className={`w-${iosWidth} mt-m`}
            />
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=com.yoodlize"
            onClick={() => this.trackStoreClick('Google')}
          >
            <img
              alt="Get it on Google Play"
              src="https://play.google.com/intl/en_us/badges/images/generic/en_badge_web_generic.png"
              className={`w-${widthBase} mt-m`}
            />
          </a>
        </div>
      </div>
    );

    const buttonContainerStr = this.isBottom
      ? `f f-cc white`
      : `fixed bottom-0 left-0 right-0 bg-white f-cc b-0 bt-1 b-coolGrayLightest ${isIos ? 'pb-l' : 'pb-s'}`;
    // <a href="https://play.google.com/store/com.yoodlize?utm_source=web-home&pcampaignid=MKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1">

    return (
      <div className={`f f-sc f-1 relative bg-primaryDark f-rows`}>
        <div className={`f f-sc f-1 f-rows`} style={{ height: '100vh' }}>
          <Whitespace small />
          <div className="">
            <img src={`/images/logo/${'logo-light.svg'}`} className={`w-${Math.floor(widthBase * 1.25)}`} />
          </div>
          <div className="f f-cc f-rows">
            <Text t-lxx white>
              Rent anything.
            </Text>
          </div>
          <Whitespace small />
          <div>
            <img src={imgSrc} className={`w-${windowWidth}`} />
          </div>
        </div>
        <div className="bg-white">
          <div className="collectively bg-white px-l">
            <Whitespace xxlarge />
            <Text noclip h1 primaryDark lineHeight="40px">
              Collectively, we own everything
            </Text>
            <Whitespace smaller2 />
            <Text noclip t-lx>
              That’s why there’s Yoodlize, the rental platform for stuff! With Yoodlize, renting items to or from your
              community is safe, secure, and fun!
            </Text>
            <Whitespace xxlarge />
          </div>
          {
            //   <div className="map bg-white f f-cc">
            //   <div className="f f-cc">
            //     <img
            //       src="/images/education/how-it-works-map.png"
            //       className="cover"
            //     />
            //   </div>
            // </div>
          }
          <div className="howitworks px-l bg-offWhite">
            <Whitespace xxlarge />
            <Text noclip h1 primaryDark>
              How it works
            </Text>
            <Whitespace medium />
            <iframe
              src="https://player.vimeo.com/video/335912641"
              width={windowWidth * 0.9}
              height={windowWidth * 0.5}
              frameBorder="0"
              allow="autoplay"
            />
            <Whitespace xxlarge />
          </div>

          <div className="covered px-l">
            <Whitespace xxlarge />
            <Text noclip h1 primaryDark>
              We’ve got your back
            </Text>
            <Whitespace smaller2 />
            <Text noclip t-lx>
              Here's why renting out your stuff is worry-free with Yoodlize:
              <Whitespace small />
              <Text bold noclip t-lx>
                <ul>
                  <li>Safe and secure in-app transaction system.</li>
                  <Whitespace small />
                  <li>Users verified by phone, email, and government I.D.</li>
                  <Whitespace small />
                  <li>All verified transactions are covered by our $2000 guarantee</li>
                  <Whitespace small />
                </ul>
              </Text>
            </Text>
            <Whitespace medium />
            <div className="f f-cc">
              <img src="images/shield.svg" className="h-182 w-203" />
            </div>
            <Whitespace xxlarge />
          </div>
          <div className="math px-l bg-offWhite">
            <Whitespace xxlarge />
            <Text noclip h1 primaryDark>
              List stuff, earn cash
            </Text>
            <Whitespace smaller2 />
            <Text noclip t-lx>
              Looking to make some coin? Earn money from home by listing and renting out your unused stuff!
            </Text>
            <Whitespace medium />
            <div className="f f-cc">
              <img src="images/education/graph.png" className="w-80p" />
            </div>
            <Whitespace smaller2 />
            <Text h6 noclip>
              Potential yearly earnings based on number of rentals per month
            </Text>
            <Text t-s>*Earnings shown at $30 a rental</Text>
            <Whitespace xxlarge />
          </div>
          <div className="findthing f f-rows px-l">
            <Whitespace xxlarge />
            <Text noclip h1 primaryDark>
              Find your thing
            </Text>
            <Whitespace smaller2 />
            <Text noclip t-lx>
              Why limit yourself to the things you own? Yoodlize gives you easy access to thousands of new items and
              experiences.
            </Text>
            <Whitespace />
            <div className="f f-cc">
              <img src="/images/education/find-your-thing.png" className="w-80p" />
            </div>
            <Whitespace xxlarge />
          </div>
          <View className="getyoodlize px-l bg-primaryDark">
            <Whitespace large />

            <div className="">
              <img src={`/images/logo/${'logo-light.svg'}`} className={`w-${Math.floor(widthBase * 1.25)}`} />
            </div>

            <Text h2 white>
              Rent out your:
            </Text>
            <Typewriter
              styles="t-lxxx white"
              on={1}
              loop={1}
              data={[
                'drill  ',
                'dirt bike  ',
                'saw  ',
                'kayak  ',
                'prom dress  ',
                'drone  ',
                'ladder  ',
                'camera  ',
                'surf boat  ',
                'wedding dress  ',
                'hot tub  ',
                'power washer  ',
                'trailer  ',
              ]}
            />
            <ScrollTrigger onEnter={() => this.bottom(true)} />

            <Whitespace />

            <div className={buttonContainerStr}>
              <div className={`w-${widthBase} w-max-${widthBase} f f-rows f-sc`}>
                <div className="mx-sxx">{appButton}</div>
              </div>
            </div>
            <Whitespace large />
            <div className="f f-columns">
              <Link to="https://www.facebook.com/yoodlizeInc">
                <i className="fab fa-facebook" style={iconStyle} />
              </Link>
              <Link to="https://www.instagram.com/yoodlizeinc/">
                <i className="fab fa-twitter-square" style={iconStyle} />
              </Link>
              <Link to="https://twitter.com/Yoodlize1">
                <i className="fab fa-instagram" style={iconStyle} />
              </Link>
            </div>
            <Text white>&#169; Yoodlize 2019</Text>
            <Whitespace large />
          </View>
        </div>
      </div>
    );
  }
}

const iconStyle = {
  fontSize: 32,
  color: colors.white,
  margin: 8,
};



// WEBPACK FOOTER //
// ./src/components/App.js